import { Edit } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { DataGridPro, GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import React, { useState, useCallback, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useHistory, NavLink as RouterLink } from 'react-router-dom';

import { If } from 'app/components/if';
import { GeneralConstants } from 'app/constants/general-constants';
import { useCallbackOnProdSwitch } from 'app/hooks';
import { Threshold } from 'app/model/cris/threshold';
import { dialogService } from 'app/services/dialog.service';
import { messagingService } from 'app/services/messaging.service';
import { thresholdService } from 'app/services/threshold.service';

import { selectProdDataStatus } from '../cris.slice';

export const ThresholdListing: React.FC = () => {
    const [rows, setRows] = useState<Threshold[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const showProd = useSelector(selectProdDataStatus);
    const history = useHistory();

    const loadThresholds = useCallback(() => {
        setLoading(true);
        thresholdService.listAll({ pageSize: GeneralConstants.PAGE_SIZE_HUGE }).subscribe({
            next: (data) => setRows(data),
            complete: () => setLoading(false),
        });
    }, []);

    useCallbackOnProdSwitch(loadThresholds);

    useEffect(() => {
        loadThresholds();
    }, [loadThresholds]);

    const handleEdit = (threshold: Threshold) => {
        history.push(`/cris/thresholds/${threshold.id}/edit`);
    };

    const deployToProd = (): void => {
        dialogService.showConfirm(
            {
                message: 'Are you sure you want to deploy to production?',
                confirmText: 'Deploy',
            },
            () =>
                thresholdService.deploy().subscribe(() => {
                    messagingService.showSuccess('Deployed');
                    loadThresholds();
                }),
        );
    };

    const columns: Array<GridColDef<Threshold>> = [
        { field: 'model', headerName: 'Model', width: 200 },
        { field: 'step', headerName: 'Step', width: 200 },
        { field: 'threshold', headerName: 'Threshold', width: 200 },
        !showProd && {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 80,
            getActions: (params: GridRowParams<Threshold>) => [
                <GridActionsCellItem icon={<Edit />} label='Edit' onClick={() => handleEdit(params.row)} />,
            ],
        },
    ].filter(Boolean) as GridColDef[];

    return (
        <>
            <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='h5'>Manage Thresholds</Typography>
                <If condition={!showProd}>
                    <Box>
                        <Button
                            variant='outlined'
                            color='warning'
                            component={RouterLink}
                            to='/cris/thresholds/new/create'
                            className='mr-2'
                        >
                            Create Thresholds
                        </Button>
                        <Button variant='contained' color='primary' onClick={deployToProd}>
                            Deploy to Prod
                        </Button>
                    </Box>
                </If>
            </Box>
            <DataGridPro<Threshold>
                columns={columns}
                rows={rows}
                loading={loading}
                autoHeight
                density='compact'
                disableRowSelectionOnClick
            />
        </>
    );
};
